import { useAuth } from '@/lib/AuthContext';
import { useLicenseStatus } from '@/hooks/useLicenseStatus';
import { Loader2 } from 'lucide-react';
import LicenseBlockedPage from '@/pages/LicenseBlockedPage';

/**
 * LicenseGuard component
 * Wraps the app and shows license blocking page when:
 * - Branch subscription_status is 'expired'
 * - Branch has more than 10 children
 *
 * Super admins bypass the license check.
 */
export default function LicenseGuard({ children }) {
  const { userProfile, isAuthenticated, authLoading } = useAuth();
  const { isBlocked, isLoading, branch, billing, childCount, billingType, monthlyPrice, branchId } = useLicenseStatus();

  // Show loading spinner while auth is initializing (e.g., after Stripe redirect)
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Don't check license for unauthenticated users (they'll be redirected to login)
  if (!isAuthenticated) {
    return children;
  }

  // Super admins bypass license check
  const isSuperAdmin = userProfile?.primary_role === 'super_admin' ||
                       userProfile?.active_role === 'super_admin';
  if (isSuperAdmin) {
    return children;
  }

  // Users without a branch (shouldn't happen, but fail-safe)
  if (!branchId) {
    return children;
  }

  // Show loading state while checking license
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // If blocked, show blocking page
  if (isBlocked) {
    const isAdmin = userProfile?.active_role === 'branch_admin' ||
                    userProfile?.primary_role === 'branch_admin';

    return (
      <LicenseBlockedPage
        branch={branch}
        billing={billing}
        childCount={childCount}
        billingType={billingType}
        monthlyPrice={monthlyPrice}
        isAdmin={isAdmin}
        userRole={userProfile?.active_role || userProfile?.primary_role}
      />
    );
  }

  // Not blocked, render children (rest of app)
  return children;
}
